import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus } from "lucide-react";
import { toast } from "sonner";

/** Phase 5 — Promoter mobile: history of own submitted Stock & Display reports. */
export default function PromoterMyStockReports() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/promoter/stock-reports")
      .then(({ data }) => setRows(data || []))
      .catch((e) => toast.error("Could not load", { description: formatApiErrorDetail(e.response?.data?.detail) }))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-3" data-testid="my-stock-page">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Stock &amp; Display</h1>
          <p className="text-xs text-slate-500 mt-0.5">Your submitted reports.</p>
        </div>
        <Button onClick={() => navigate("/promoter/stock/new")}
                className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="add-stock-btn">
          <Plus size={14} className="mr-1" /> New
        </Button>
      </header>

      {loading && <div className="text-sm text-slate-500" data-testid="my-stock-loading">Loading…</div>}

      {!loading && rows.length === 0 && (
        <Card>
          <CardContent className="p-6 text-center text-sm text-slate-500" data-testid="my-stock-empty">
            No reports yet. Tap “New” to submit your first stock &amp; display check.
          </CardContent>
        </Card>
      )}

      <div className="space-y-2">
        {rows.map((r) => (
          <Card key={r.id} data-testid={`my-stock-row-${r.id.slice(0, 8)}`}>
            <CardContent className="p-3 space-y-1">
              <div className="flex items-start justify-between">
                <div className="text-xs font-mono text-slate-500">{r.report_date}</div>
                <ConditionPill c={r.display_condition} />
              </div>
              <div className="text-sm font-semibold text-slate-900">
                {r.brand} {r.product_model}
                {r.product_category && <span className="text-slate-500 font-normal"> · {r.product_category}</span>}
              </div>
              <div className="text-xs text-slate-600">
                Stock {r.available_stock_qty} · Display {r.display_qty}
                {r.required_replenishment_qty != null && r.required_replenishment_qty > 0
                  ? ` · Replenish ${r.required_replenishment_qty}` : ""}
              </div>
              {r.remarks && <div className="text-[11px] text-slate-500 truncate">{r.remarks}</div>}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

function ConditionPill({ c }) {
  const m = {
    good: "bg-emerald-100 text-emerald-800",
    average: "bg-amber-100 text-amber-800",
    poor: "bg-rose-100 text-rose-800",
  }[c] || "bg-slate-100 text-slate-700";
  return <Badge className={`${m} text-[10px]`}>{c || "—"}</Badge>;
}
